'use client'

import React, { useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { cn } from '@/lib/utils'
import { Logomark } from '@/components/brand'
import { Icons } from '@/components/ui/icons'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  isAdminRoles,
  sectionTitleForHref,
  visibleNavSections,
} from '@/lib/nav-items'
import { useShellCounts } from '@/hooks/useShellCounts'
import { PRODUCT_SUBTITLE } from '@/lib/naming'

interface MobileSidebarProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * The phone drawer. It reads the same list as the rail (lib/nav-items.ts) with
 * the same role filter and the same folded groups, so every page is reachable
 * on a phone. Tapping a link closes the drawer.
 */
export function MobileSidebar({ isOpen, onClose }: MobileSidebarProps) {
  const { data: session } = useSession()
  const pathname = usePathname()
  const counts = useShellCounts()
  const [openSections, setOpenSections] = useState<string[]>([])

  const sections = visibleNavSections(isAdminRoles(session?.roles))
  const activeSection = sectionTitleForHref(pathname)

  const toggle = (title: string) =>
    setOpenSections((current) =>
      current.includes(title) ? current.filter((t) => t !== title) : [...current, title]
    )

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent
        side='left'
        className='w-72 border-r-0 bg-brand-navy p-0 text-white md:hidden [&>button]:text-white'
      >
        <SheetHeader className='flex h-20 flex-row items-center border-b border-white/10 px-4'>
          <Link href='/' className='flex items-center gap-3' onClick={onClose}>
            <span className='flex h-10 w-10 items-center justify-center rounded-xl bg-white'>
              <Logomark variant='dark' size={24} />
            </span>
            <span className='flex flex-col text-left'>
              <SheetTitle className='font-display text-[17px] font-semibold leading-tight text-white'>
                Supervity
              </SheetTitle>
              <span className='text-xs font-semibold uppercase tracking-[0.12em] text-white/55'>
                {PRODUCT_SUBTITLE}
              </span>
            </span>
          </Link>
        </SheetHeader>

        <nav className='flex-1 overflow-y-auto px-4 py-4' aria-label='Mobile navigation'>
          {sections.map((section, sectionIndex) => {
            const open =
              !section.collapsedByDefault ||
              openSections.includes(section.title) ||
              activeSection === section.title
            const titleClass =
              'px-3 text-xs font-semibold uppercase tracking-[0.14em] text-white/55'
            return (
              <div key={section.title} className={cn(sectionIndex > 0 && 'mt-5')}>
                {section.collapsedByDefault ? (
                  <button
                    type='button'
                    aria-expanded={open}
                    onClick={() => toggle(section.title)}
                    className={cn(titleClass, 'mb-1.5 flex min-h-11 w-full items-center gap-1.5 text-left')}
                  >
                    {section.title}
                    {open ? (
        <Icons.chevronDown className='h-3.5 w-3.5 shrink-0' aria-hidden='true' />
      ) : (
        <Icons.chevronRight className='h-3.5 w-3.5 shrink-0' aria-hidden='true' />
      )}
                  </button>
                ) : (
                  <p className={cn(titleClass, 'mb-1.5')}>{section.title}</p>
                )}
                {open && (
                  <div className='space-y-0.5'>
                    {section.items.map((item) => {
                      const active = pathname === item.href
                      const badge = item.badgeKey ? counts[item.badgeKey] : undefined
                      const Icon = item.icon
                      return (
                        <Link
                          key={item.href}
                          href={item.href}
                          onClick={onClose}
                          aria-current={active ? 'page' : undefined}
                          className={cn(
                            'relative flex min-h-11 items-center gap-3 rounded-xl px-3 py-2.5 text-base font-medium',
                            active
                              ? 'bg-white/[0.14] text-white'
                              : 'text-white/70 hover:bg-white/[0.07] hover:text-white'
                          )}
                        >
                          {active && (
                            <span
                              aria-hidden='true'
                              className='absolute bottom-1.5 left-0 top-1.5 w-[3px] rounded-full bg-brand-cornflower'
                            />
                          )}
                          <Icon strokeWidth={1.5} aria-hidden='true' className='h-5 w-5 shrink-0 text-white/60' />
                          <span className='flex-1'>{item.label}</span>
                          {badge !== undefined && badge > 0 && (
                            <>
                              <span
                                aria-hidden='true'
                                className='flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-cornflower/30 px-1.5 text-xs font-semibold text-white'
                              >
                                {badge > 99 ? '99+' : badge}
                              </span>
                              <span className='sr-only'>, {badge} waiting</span>
                            </>
                          )}
                        </Link>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </nav>
      </SheetContent>
    </Sheet>
  )
}
